import React from 'react'
import { Moon, Sun } from 'lucide-react'
import useThemeStore from '@/stores/theme'

interface ThemeToggleProps {
  className?: string
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '' }) => {
  const { currentTheme, toggleTheme } = useThemeStore()
  const isDark = currentTheme === 'dark'

  return (
    <button
      onClick={toggleTheme}
      className={`
        relative inline-flex h-8 w-14 items-center rounded-full 
        bg-gradient-to-r from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-600
        transition-all duration-300 ease-in-out 
        focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 
        focus:ring-offset-transparent
        hover:from-gray-300 hover:to-gray-400 dark:hover:from-slate-600 dark:hover:to-slate-500
        shadow-inner
        ${className}
      `}
      role="switch"
      aria-checked={isDark}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {/* Background track with gradient */}
      <div className={`
        absolute inset-0 rounded-full transition-all duration-300
        ${isDark 
          ? 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-100' 
          : 'bg-gradient-to-r from-yellow-300 via-orange-300 to-amber-300 opacity-0'
        }
      `} />
      
      {/* Sliding circle */}
      <div
        className={`
          relative inline-flex h-6 w-6 items-center justify-center rounded-full 
          bg-white shadow-lg transform transition-all duration-300 ease-in-out z-10
          ${isDark ? 'translate-x-7' : 'translate-x-1'}
          ring-2 ring-white/50
        `}
      >
        {/* Sun icon - visible in light mode */}
        <Sun 
          className={`
            h-3.5 w-3.5 text-amber-500 transition-all duration-300 transform
            ${isDark ? 'opacity-0 scale-75 rotate-180' : 'opacity-100 scale-100 rotate-0'}
          `}
        />
        
        {/* Moon icon - visible in dark mode */}
        <Moon 
          className={`
            absolute h-3.5 w-3.5 text-indigo-400 transition-all duration-300 transform
            ${isDark ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-75 rotate-180'}
          `}
        />
      </div>
      
      {/* Background decorative icons */}
      <div className="absolute inset-0 flex items-center justify-between px-2 pointer-events-none">
        <Sun className={`h-2.5 w-2.5 transition-all duration-300 ${isDark ? 'text-white/30 scale-75' : 'text-amber-400/60 scale-100'}`} />
        <Moon className={`h-2.5 w-2.5 transition-all duration-300 ${isDark ? 'text-indigo-300/60 scale-100' : 'text-slate-400/40 scale-75'}`} />
      </div>
    </button>
  )
}